/* eslint-disable @typescript-eslint/no-unused-vars */
import { FC, useCallback, useState, useEffect } from 'react';
import { Button, Divider, Input } from 'antd';
import {
    config,
    debounceEmail,
    enabledOauthProviders,
    providersDic,
    sendToAmplitude,
    useFormData,
    ProvidersType
} from '../model';
import { onKeyDown } from '../hooks';
import Preview from './preview.png';
import s from './index.module.css';
import { Loader } from '../components/loader';

export const StartScreen: FC = () => {
    const { email, setEmail, nextStep } = useFormData();
    const [error, setError] = useState<string>('');
    const [loading, setLoading] = useState(true);
    const [providers, setProviders] = useState<ProvidersType[]>([]);
    const [key, setKey] = useState<string>('');

    const handleDebounceEmail = useCallback(async () => {
        if (!email.length) setError('invalid');
        else {
            setError('');
            const { res } = await debounceEmail(email);

            if (res.debounce?.free_email === 'true') setError('free_email');
            else if (res.debounce?.result === 'Invalid') setError('invalid');
            else {
                sendToAmplitude('WALLARMCOM_AASM_GO_TO_SIGNUP_FORM', { email });
                nextStep('signup');
            }
        }
    }, [email]);

    const initProviders = async () => {
        const { res } = await enabledOauthProviders();

        if (res) {
            setProviders(res?.providers);
            setKey(res?.state);
        }

        setLoading(false);
    };

    useEffect(() => {
        initProviders();
    }, []);

    const goToConsole = useCallback(() => {
        sendToAmplitude('WALLARMCOM_AASM_ALREADY_REGISTERED');

        window.location.href = `${config.siteURL}`;
    }, []);

    const handleOauthSubmit = (provider: ProvidersType) => {
        sendToAmplitude(`WALLARMCOM_AASM_SSO_SIGNUP`, {
            provider: providersDic[provider]?.title
        });

        const form = document.getElementById(provider) as HTMLFormElement;

        localStorage.setItem('sso_provider', provider);
        const newWindow = window.open('');

        if (newWindow) {
            newWindow.focus();
            newWindow.document.body.style.opacity = '0';
            newWindow?.document.body.appendChild(form.cloneNode(true));
            newWindow?.document.forms[0].submit();
        }
    };

    return (
        <div className={s.content}>
            {loading && (
                <div className={s.block}>
                    <div className={s.loader}>
                        <Loader />
                    </div>
                </div>
            )}
            {!loading && (
                <>
                    <div className={s.block}>
                        <h1>
                            Get started in under a minute.
                            <br />
                            No installation required
                        </h1>

                        <div className={s.column}>
                            <Input
                                size='large'
                                placeholder='Work or SSO email'
                                onChange={e => setEmail(e.target.value)}
                                onKeyDown={e => onKeyDown(e, handleDebounceEmail)}
                            />

                            {error && (
                                <div className={s.error}>
                                    {error === 'free_email' &&
                                        'Please enter a business email. This form does not access free email providers.'}
                                    {error === 'invalid' && 'Please enter a valid email address'}
                                </div>
                            )}

                            <Button
                                type='primary'
                                htmlType='submit'
                                block
                                size='large'
                                disabled={!email}
                                onClick={handleDebounceEmail}
                            >
                                Next
                            </Button>
                        </div>

                        <p className={s.entry}>
                            Already registered?
                            <span className={s.link} onClick={goToConsole}>
                                Sign in
                            </span>
                        </p>

                        {providers.length > 0 && (
                            <>
                                <Divider>OR</Divider>

                                <div className={s.column}>
                                    {providers.map(provider => (
                                        <form
                                            key={provider}
                                            id={provider}
                                            method='post'
                                            action={`${config.apiURL}/v2/oauth/${provider}`}
                                        >
                                            <input type='hidden' name='state' value={key} />
                                            <Button
                                                type='primary'
                                                className={s.provider}
                                                size='large'
                                                block
                                                onClick={e => {
                                                    e.preventDefault();
                                                    handleOauthSubmit(provider);
                                                }}
                                            >
                                                <img
                                                    src={providersDic[provider]?.icon}
                                                    alt={provider}
                                                    className={s.icon}
                                                />
                                                Sign up with {providersDic[provider]?.title}
                                            </Button>
                                        </form>
                                    ))}
                                </div>
                            </>
                        )}

                        <p className={s.desc}>
                            By registering, I certify that I am employed by the organization for
                            which I am authorised to conduct external security scans with AASM.
                        </p>
                    </div>

                    <div className={s.block}>
                        <h2>Prevent vulnerabilities & API Leaks with AASM</h2>

                        <p>
                            API Attack Surface Management (AASM) is an agentless detection solution
                            tailored to the API ecosystem, designed to discover all external hosts
                            with their APIs, identify missing WAF/WAAP solutions and mitigate API
                            Leaks
                        </p>

                        <img src={Preview} alt='logo' />
                    </div>
                </>
            )}
        </div>
    );
};
